import {useContext} from 'react';

import {ErrorMessage, Field, FormikProvider, useFormik} from 'formik';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';

import {FormControl, Grid, SxProps, Typography, useTheme} from '@mui/material';

import {EmpionButton} from '../../../../../../../components/EmpionButton/EmpionButton';
import {EmpionPaper} from '../../../../../../../components/EmpionPaper/EmpionPaper';
import {EmpionSelectField} from '../../../../../../../components/EmpionSelectField/EmpionSelectField';
import {LanguageContext} from '../../../../../../../contexts/LanguageContext/LanguageContext';
import {empionGreen, empionOrange} from '../../../../../../../layouts/theme/EmpionColors';
import {isQuestionnairePage} from '../../../../../../../utils/questionnaireUtils/isQuestionnairePage';
import {StrengthsItem} from '../../../types/SurveyQuestionnaireTypes';

interface TopStrengthsProps {
  onSubmit: (answers: string[]) => void;
  strengths: StrengthsItem[];
}

export const TopStrengths = ({onSubmit, strengths}: TopStrengthsProps) => {
  const {t} = useTranslation();

  const currentPage = useLocation();
  const {pathname} = currentPage;

  const theme = useTheme();

  const currentLanguage = useContext(LanguageContext);

  const paperStyles: SxProps = {
    backgroundColor: 'white',
    borderRadius: '16px',
    width: '100%',
    height: 'fit-content',
    padding: {xs: 1, sm: 3},
    marginTop: {xs: '2rem', md: '4rem'},
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };

  const isQuestionnairePageStyles: SxProps = {
    borderRadius: '4px',
    backgroundImage: 'none',
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.mode === 'dark' ? empionGreen[100] : empionOrange[4],
      color: theme.palette.mode === 'dark' ? theme.palette.text.primary : empionGreen[700],
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? empionGreen[100] : empionOrange[4],
      },
      '& fieldset': {
        borderColor: theme.palette.mode === 'dark' ? empionGreen[100] : empionOrange[4],
      },
      '&:hover fieldset': {
        borderColor: theme.palette.mode === 'dark' ? empionGreen[100] : empionOrange[4],
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.mode === 'dark' ? empionGreen[100] : empionOrange[4],
      },
      '& .MuiOutlinedInput-input': {
        caretColor: theme.palette.text.primary,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.primary,
      '&.Mui-focused': {
        color: theme.palette.text.primary,
      },
    },
    '& .Mui-selected': {
      backgroundColor: theme.palette.mode === 'dark' ? empionGreen[600] : empionGreen[40],
    },
  };

  const formik = useFormik({
    initialValues: {
      topStrength1: '',
      topStrength2: '',
      topStrength3: '',
    },
    onSubmit: async (values) => {
      const payloadArray = [values.topStrength1, values.topStrength2, values.topStrength3];

      onSubmit(payloadArray);
    },

    validate: (values) => {
      const errors: any = {};

      if (!values.topStrength1) {
        errors.topStrength1 = t('shared.required');
      }

      if (!values.topStrength2) {
        errors.topStrength2 = t('shared.required');
      }

      if (!values.topStrength3) {
        errors.topStrength3 = t('shared.required');
      }

      return errors;
    },
  });

  const handleOptions = (options: StrengthsItem[] | undefined, excludeKeys: string[]) => {
    const listOfOptions = options
      ?.filter((option) => !excludeKeys.includes(option.strengthKey))
      .map((option: StrengthsItem) => {
        return {
          id: option.strengthKey,
          label: option.name[currentLanguage],
        };
      });

    // Sort the list of options alphabetically by label
    return listOfOptions?.sort((a, b) => {
      const labelA = a.label.toLowerCase();
      const labelB = b.label.toLowerCase();

      if (labelA < labelB) {
        return -1;
      }
      if (labelA > labelB) {
        return 1;
      }
      return 0;
    });
  };

  return (
    <Grid container justifyContent='center' alignItems='center' textAlign='center'>
      <EmpionPaper sx={paperStyles}>
        <FormikProvider value={formik}>
          <FormControl>
            <form onSubmit={formik.handleSubmit}>
              <Grid
                container
                spacing={2}
                sx={{
                  mt: {sm: 2},
                }}
              >
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginBottom: 4,
                  }}
                >
                  <Typography
                    variant='h1'
                    gutterBottom
                    textTransform={'uppercase'}
                    align={'center'}
                    sx={{
                      fontSize: {xs: '2.4rem', sm: '3rem', md: '3rem', lg: '4.5rem'},
                      lineHeight: {xs: '2.5rem', sm: '3rem', md: '3rem', lg: '4.5rem'},
                      color: empionGreen[700],
                    }}
                  >
                    {t('companySurveys.public.topStrengths.title')}
                  </Typography>
                  <Typography
                    variant='h4'
                    textAlign={'center'}
                    sx={{
                      color: empionGreen[500],
                    }}
                  >
                    {t('companySurveys.public.topStrengths.description')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Field
                    type='select'
                    name='topStrength1'
                    component={EmpionSelectField}
                    options={handleOptions(strengths, [formik.values.topStrength2, formik.values.topStrength3])}
                    size={'small'}
                    sx={isQuestionnairePage(pathname) ? isQuestionnairePageStyles : {}}
                    isQuestionnairePage={isQuestionnairePage(pathname)}
                  />
                  <Typography variant='body2' color='error' marginTop={1}>
                    <ErrorMessage name='topStrength1' component='span' />
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Field
                    type='select'
                    name='topStrength2'
                    component={EmpionSelectField}
                    options={handleOptions(strengths, [formik.values.topStrength1, formik.values.topStrength3])}
                    size={'small'}
                    sx={isQuestionnairePage(pathname) ? isQuestionnairePageStyles : {}}
                    isQuestionnairePage={isQuestionnairePage(pathname)}
                  />
                  <Typography variant='body2' color='error' marginTop={1}>
                    <ErrorMessage name='topStrength2' component='span' />
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Field
                    type='select'
                    name='topStrength3'
                    component={EmpionSelectField}
                    options={handleOptions(strengths, [formik.values.topStrength1, formik.values.topStrength2])}
                    size={'small'}
                    sx={isQuestionnairePage(pathname) ? isQuestionnairePageStyles : {}}
                    isQuestionnairePage={isQuestionnairePage(pathname)}
                  />
                  <Typography variant='body2' color='error' marginTop={1}>
                    <ErrorMessage name='topStrength3' component='span' />
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} sx={{textAlign: 'center', mt: {xs: 5, sm: 3}}}>
                <EmpionButton type='submit'>{t('shared.formSubmit.submit')}</EmpionButton>
              </Grid>
            </form>
          </FormControl>
        </FormikProvider>
      </EmpionPaper>
    </Grid>
  );
};
